/* Save as PDF asks first: save the sheet as it is, or customize which parts the printed copy
   leaves empty, to fill in by hand at the table. */

import { useState } from 'react';
import { Backpack, ChevronsUp, CircleDot, Coins, Diamond, Download, Eye, EyeOff, Flame, Heart, Hexagon, LucideIcon, ScrollText, Shield, Shirt, SlidersHorizontal, Swords, Wind } from 'lucide-react';
import Dialog from './Dialog';
import { toggle } from './BuilderParts';
import { BLANK_PARTS, BlankPart } from './model';

const LOOK: Record<BlankPart, { icon: LucideIcon; hint: string }> = {
  'Hit Points': { icon: Heart, hint: 'HP boxes' },
  Stress: { icon: Flame, hint: 'Stress boxes' },
  Hope: { icon: Diamond, hint: 'Hope diamonds' },
  'Armor Slots': { icon: Shield, hint: 'Marked Armor Slots' },
  Gold: { icon: Coins, hint: 'Handfuls, bags and chest' },
  'Trait marks': { icon: CircleDot, hint: 'To clear at a new tier' },
  Level: { icon: ChevronsUp, hint: 'The level number' },
  Traits: { icon: Hexagon, hint: 'The six trait numbers' },
  Evasion: { icon: Wind, hint: 'The number in the shield' },
  Experience: { icon: ScrollText, hint: 'Names and bonuses' },
  'Active weapons': { icon: Swords, hint: 'Primary and secondary' },
  'Active armor': { icon: Shirt, hint: 'With its score and thresholds' },
  Inventory: { icon: Backpack, hint: 'Every inventory line' },
};

const SCORES: BlankPart[] = ['Level', 'Traits', 'Evasion', 'Experience'];
const GEAR: BlankPart[] = ['Active weapons', 'Active armor', 'Inventory'];
const GROUPS = [
  { title: 'Marked in play', parts: BLANK_PARTS.filter((p) => !SCORES.includes(p) && !GEAR.includes(p)) },
  { title: 'Scores & Experiences', parts: SCORES },
  { title: 'Gear', parts: GEAR },
];

interface Props {
  /** The parts left empty last time, picked again when customizing. */
  initial: BlankPart[];
  /** A reminder about how a page prints (the Martial Stances page's half or full page). */
  note?: string;
  onSave: (blank: BlankPart[]) => void;
  onClose: () => void;
}

export default function SavePdfDialog({ initial, note, onSave, onClose }: Props) {
  const [customizing, setCustomizing] = useState(false);
  const [blank, setBlank] = useState(initial);
  const save = (
    <button type="button" className="cb-btn cb-btn--primary" onClick={() => onSave(customizing ? blank : [])}>
      <Download size={16} aria-hidden="true" />{customizing ? 'Save PDF' : 'Save now'}
    </button>
  );

  return (
    <Dialog
      title="Save as PDF"
      role="alertdialog"
      onClose={onClose}
      footer={customizing ? (
        <>
          <span className="cb-dialog__progress">{blank.length ? `${blank.length} left empty` : 'Everything filled in'}</span>
          {save}
        </>
      ) : (
        <>
          <button type="button" className="cb-btn" onClick={() => setCustomizing(true)}>
            <SlidersHorizontal size={16} aria-hidden="true" />Customize
          </button>
          {save}
        </>
      )}
    >
      {customizing ? (
        <div className="cb-pdf">
          <p className="cb-pdf__lead">Tap a part to leave it empty on the printed sheet, ready for a pencil.</p>
          <div className="cb-pdf__all">
            <button type="button" className="cb-btn" disabled={blank.length === BLANK_PARTS.length} onClick={() => setBlank([...BLANK_PARTS])}>
              <EyeOff size={16} aria-hidden="true" />Hide all
            </button>
            <button type="button" className="cb-btn" disabled={!blank.length} onClick={() => setBlank([])}>
              <Eye size={16} aria-hidden="true" />Keep all
            </button>
          </div>
          {GROUPS.map(({ title, parts }) => (
            <section key={title} className="cb-pdf__group" aria-label={title}>
              <h3 className="cb-pdf__title">{title}</h3>
              <div className="cb-pdf__grid">
                {parts.map((part) => {
                  const { icon: Icon, hint } = LOOK[part];
                  const empty = blank.includes(part);
                  return (
                    <button
                      key={part}
                      type="button"
                      className={`cb-pdf__part${empty ? ' is-empty' : ''}`}
                      aria-label={part}
                      aria-pressed={empty}
                      onClick={() => setBlank((list) => toggle(list, part) as BlankPart[])}
                    >
                      <span className="cb-pdf__icon" aria-hidden="true"><Icon size={18} /></span>
                      <span className="cb-pdf__text" aria-hidden="true">
                        <span className="cb-pdf__name">{part}</span>
                        <span className="cb-pdf__hint">{hint}</span>
                      </span>
                      {/* A gold gem while kept, an "Empty" tag once left out */}
                      <span className="cb-pdf__state" aria-hidden="true">{empty ? 'Empty' : ''}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="cb-pdf-ask">
          <p className="cb-pdf-ask__question">Save the sheet as it is?</p>
          <p className="cb-pdf__lead">Or customize it: leave parts like Hope, weapons or inventory empty to fill in by hand after printing.</p>
          {note && <p className="cb-pdf__note">{note}</p>}
        </div>
      )}
    </Dialog>
  );
}
